import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import React, { useState } from "react";
import { Popover, Flex, Box, Dropdown, Button, Divider, Switch, Thumbnail, Tooltip, Toolbar, Badge } from "@bynder/design-system";
import { IconSingleSelect, IconDeleteOutline, IconMore, IconNotifications, IconStateIndicatorM } from "@bynder/icons";
import { InboxWrapper, StickyHeader, InboxTitle, DropdownWrapper, InboxHeader, InboxBody, ListStyled, ListItemStyled, FlexWrapper, BoxGrow, Title, Subtitle, Created, ActionsWrapper, EmptyStateWrapper } from "./styles.js";
import { notificationTypeList, hoursAgo } from "./utils.js";
import useInboxNotifications from "./useInboxNotifications.js";
function InboxList() {
  const {
    inboxNotifications,
    missingTypes,
    unreadCount,
    isLoading,
    readNotification,
    deleteNotification,
    filterRead,
    selectedType,
    setFilterRead,
    setSelectedType,
    resetState,
    readAllNotification,
    deleteAllNotification
  } = useInboxNotifications();
  const [listItemHovered, setListItemHovered] = useState(null);
  if (isLoading) {
    return null;
  }
  const isDisabled = inboxNotifications.length === 0 && !filterRead && selectedType === null;
  function markAsRead(event, notification) {
    event.stopPropagation();
    readNotification(notification);
  }
  function markAsDeleted(event, notification) {
    event.stopPropagation();
    deleteNotification(notification);
  }
  return /* @__PURE__ */ jsx(
    Popover,
    {
      "aria-label": "Notifications",
      onClose: resetState,
      trigger: ({ isOpen, ...triggerProps }) => /* @__PURE__ */ jsx(
        Button,
        {
          isPressed: isOpen,
          title: "Notifications",
          "aria-label": "Notifications",
          variant: "clean",
          icon: /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(IconNotifications, {}),
            /* @__PURE__ */ jsx(Badge, { isPillShaped: true, children: unreadCount })
          ] }),
          "data-testid": "hybrid-notification-center",
          isSmall: true,
          ...triggerProps
        }
      ),
      position: "bottom-right",
      noPadding: true,
      children: /* @__PURE__ */ jsxs(InboxWrapper, { children: [
        /* @__PURE__ */ jsxs(StickyHeader, { children: [
          /* @__PURE__ */ jsxs(Flex, { justifyContent: "space-between", alignItems: "center", children: [
            /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(InboxTitle, { children: "Notifications" }) }),
            /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(DropdownWrapper, { children: /* @__PURE__ */ jsxs(
              Dropdown,
              {
                "aria-label": "Show More",
                position: "top-right",
                trigger: ({ isOpen, ...triggerProps }) => /* @__PURE__ */ jsx(
                  Button,
                  {
                    isPressed: isOpen,
                    title: "More",
                    icon: /* @__PURE__ */ jsx(IconMore, {}),
                    variant: "clean",
                    ...triggerProps
                  }
                ),
                children: [
                  /* @__PURE__ */ jsx(
                    Dropdown.Item,
                    {
                      onClick: readAllNotification,
                      icon: /* @__PURE__ */ jsx(IconSingleSelect, {}),
                      children: "Mark all as read"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Dropdown.Item,
                    {
                      icon: /* @__PURE__ */ jsx(IconDeleteOutline, {}),
                      onClick: deleteAllNotification,
                      children: "Delete all"
                    }
                  )
                ]
              }
            ) }) })
          ] }),
          /* @__PURE__ */ jsx(Divider, {}),
          /* @__PURE__ */ jsx(InboxHeader, { children: /* @__PURE__ */ jsxs(Flex, { justifyContent: "space-between", alignItems: "center", children: [
            /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
              Dropdown,
              {
                "aria-label": "Filter notifications by type",
                isClosedOnScroll: true,
                trigger: ({ isOpen, ...triggerProps }) => /* @__PURE__ */ jsx(
                  Button,
                  {
                    isDisabled,
                    isPressed: isOpen,
                    rightIcon: /* @__PURE__ */ jsx(Dropdown.Arrow, {}),
                    ...triggerProps,
                    "aria-label": "Notification Types",
                    children: selectedType === null ? "All types" : selectedType
                  }
                ),
                children: notificationTypeList.map((type, index) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
                  index === 1 && /* @__PURE__ */ jsx(Dropdown.Divider, {}),
                  /* @__PURE__ */ jsx(
                    Dropdown.Item,
                    {
                      isDisabled: missingTypes.includes(type.label),
                      isChecked: type.label === selectedType,
                      onClick: () => setSelectedType(
                        type.key === "all" ? null : type.label
                      ),
                      children: type.label
                    },
                    type.key
                  )
                ] }, type.key))
              }
            ) }),
            /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
              Switch,
              {
                isDisabled,
                isChecked: filterRead,
                "aria-label": "Only unread notifications",
                onChange: () => {
                  setFilterRead(!filterRead);
                },
                children: "Only unread"
              }
            ) })
          ] }) }),
          /* @__PURE__ */ jsx(Divider, {})
        ] }),
        /* @__PURE__ */ jsx(InboxBody, { children: inboxNotifications.length > 0 ? /* @__PURE__ */ jsx(ListStyled, { children: inboxNotifications.map((notification) => {
          return /* @__PURE__ */ jsx(
            "div",
            {
              onMouseOver: () => {
                setListItemHovered(notification.id.toString());
              },
              onMouseLeave: () => setListItemHovered(null),
              children: /* @__PURE__ */ jsx(
                ListItemStyled,
                {
                  onClick: () => {
                    return false;
                  },
                  children: /* @__PURE__ */ jsx(FlexWrapper, { children: /* @__PURE__ */ jsxs(Flex, { gap: "5", children: [
                    /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
                      Thumbnail,
                      {
                        size: "xs",
                        shape: "square",
                        icon: /* @__PURE__ */ jsx(IconNotifications, {}),
                        variant: "clean",
                        iconColor: "blue500"
                      }
                    ) }),
                    /* @__PURE__ */ jsxs(BoxGrow, { children: [
                      /* @__PURE__ */ jsx(Title, { children: notification.title }),
                      /* @__PURE__ */ jsx(Subtitle, { children: notification.type }),
                      /* @__PURE__ */ jsx(Created, { children: /* @__PURE__ */ jsx(Tooltip, { content: notification.created, children: hoursAgo(notification.created) }) })
                    ] }),
                    /* @__PURE__ */ jsxs(Box, { children: [
                      listItemHovered === notification.id && /* @__PURE__ */ jsx(ActionsWrapper, { children: /* @__PURE__ */ jsxs(Toolbar, { isSmall: true, children: [
                        notification.read === null && /* @__PURE__ */ jsxs(Fragment, { children: [
                          /* @__PURE__ */ jsx(
                            Tooltip,
                            {
                              "aria-label": "Delete",
                              content: "Mark as read",
                              position: "top-right",
                              children: /* @__PURE__ */ jsx(
                                Toolbar.Button,
                                {
                                  title: "Mark as read",
                                  icon: /* @__PURE__ */ jsx(IconSingleSelect, {}),
                                  onClick: (event) => markAsRead(
                                    event,
                                    notification
                                  )
                                }
                              )
                            }
                          ),
                          /* @__PURE__ */ jsx(Toolbar.Divider, {})
                        ] }),
                        /* @__PURE__ */ jsx(
                          Tooltip,
                          {
                            "aria-label": "Delete",
                            content: "Delete",
                            position: "top-right",
                            children: /* @__PURE__ */ jsx(
                              Toolbar.Button,
                              {
                                onClick: (event) => markAsDeleted(
                                  event,
                                  notification
                                ),
                                title: "Delete",
                                icon: /* @__PURE__ */ jsx(IconDeleteOutline, {})
                              }
                            )
                          }
                        )
                      ] }) }),
                      notification.read === null && /* @__PURE__ */ jsx(
                        Thumbnail,
                        {
                          size: "xs",
                          shape: "circle",
                          icon: /* @__PURE__ */ jsx(IconStateIndicatorM, {}),
                          backgroundColor: "transparent",
                          iconColor: "blue500"
                        }
                      )
                    ] })
                  ] }) })
                }
              )
            },
            notification.id
          );
        }) }) : /* @__PURE__ */ jsx(EmptyStateWrapper, { children: "You don’t have any notifications" }) })
      ] })
    }
  );
}
export {
  InboxList as default
};
//# sourceMappingURL=InboxList.js.map
